import { useState, useEffect, useRef } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";

export interface UnitTypeDistribution {
  type: string;
  count: number;
  subtypes: {
    subtype: string;
    count: number;
  }[];
}

export interface UnitTypeMetrics {
  totalUnits: number;
  typeDistribution: UnitTypeDistribution[];
  subtypeDistribution: {
    subtype: string;
    count: number;
    type: string;
  }[];
}

interface UseUnitTypeDistributionReturn {
  metrics: UnitTypeMetrics | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// Global cache to prevent re-fetches on tab switch
let globalCache: UnitTypeMetrics | null = null;
let globalLoading = false;
let globalError: string | null = null;
const subscribers = new Set<() => void>();

export const useUnitTypeDistribution = (): UseUnitTypeDistributionReturn => {
  const { session } = useSession();
  const [metrics, setMetrics] = useState<UnitTypeMetrics | null>(globalCache);
  const [isLoading, setIsLoading] = useState(globalLoading);
  const [error, setError] = useState<string | null>(globalError);
  const fetchInProgressRef = useRef(false);

  const notifySubscribers = () => {
    subscribers.forEach((callback) => callback());
  };

  const fetchData = async (force = false) => {
    if (!session) return;
    if (globalCache && !force) return;
    if (fetchInProgressRef.current) return;

    try {
      fetchInProgressRef.current = true;
      globalLoading = true;
      globalError = null;
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/units/dynamic`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            relations: ["building"],
          }),
        },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch units: ${response.status}`);
      }

      const units = await response.json();

      // Filter to only owned units (exclude acquisition/due diligence)
      const ownedUnits = units.filter((unit: any) => unit.building?.owner === true);

      // Calculate type distribution
      const typeMap = new Map<string, { count: number; subtypes: Map<string, number> }>();

      ownedUnits.forEach((unit: any) => {
        const type = unit.unitType || "Onbekend";
        const subtype = unit.unitSubtype || "Onbekend";

        if (!typeMap.has(type)) {
          typeMap.set(type, { count: 0, subtypes: new Map() });
        }

        const typeData = typeMap.get(type)!;
        typeData.count++;

        if (!typeData.subtypes.has(subtype)) {
          typeData.subtypes.set(subtype, 0);
        }
        typeData.subtypes.set(subtype, typeData.subtypes.get(subtype)! + 1);
      });

      // Convert to array format
      const typeDistribution: UnitTypeDistribution[] = [];
      const subtypeDistribution: { subtype: string; count: number; type: string }[] = [];

      // Sort types by count descending
      const sortedTypes = Array.from(typeMap.entries()).sort(
        (a, b) => b[1].count - a[1].count
      );

      sortedTypes.forEach(([type, data]) => {
        const subtypes = Array.from(data.subtypes.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([subtype, count]) => ({ subtype, count }));

        typeDistribution.push({
          type,
          count: data.count,
          subtypes,
        });

        subtypes.forEach(({ subtype, count }) => {
          subtypeDistribution.push({ subtype, count, type });
        });
      });

      // Sort subtypes by count descending
      subtypeDistribution.sort((a, b) => b.count - a.count);

      const calculatedMetrics: UnitTypeMetrics = {
        totalUnits: ownedUnits.length,
        typeDistribution,
        subtypeDistribution,
      };

      globalCache = calculatedMetrics;
      globalLoading = false;

      setMetrics(calculatedMetrics);
      setIsLoading(false);
      notifySubscribers();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch unit data";
      globalError = errorMessage;
      globalLoading = false;

      setError(errorMessage);
      setIsLoading(false);
      notifySubscribers();
    } finally {
      fetchInProgressRef.current = false;
    }
  };

  const refresh = async () => {
    globalCache = null;
    await fetchData(true);
  };

  useEffect(() => {
    const updateState = () => {
      if (globalCache) setMetrics(globalCache);
      setIsLoading(globalLoading);
      setError(globalError);
    };

    subscribers.add(updateState);

    if (!globalCache && !globalLoading && session) {
      fetchData();
    } else if (globalCache) {
      setMetrics(globalCache);
      setIsLoading(false);
      setError(null);
    }

    return () => {
      subscribers.delete(updateState);
    };
  }, [session]);

  return {
    metrics,
    isLoading,
    error,
    refresh,
  };
};
